import React, { useState, useEffect, useCallback, useRef } from 'react';
import config from '../Config';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { formatEasternDate } from '../Utils/dateTime';
import { formatCentsAsCurrency } from '../Utils/money';
import { getOrderFinancialsMaster } from '../Config/adminOrderFlowService';
// import { getOrderPaymentsLedger } from '../Config/adminOrderFlowService';

function getCustomerDisplayLabel(order) {
  const orgName =
    order?.organization_name?.trim() ||
    order?.organization?.name?.trim() ||
    order?.organization?.business_name?.trim() ||
    '';
  const customerName =
    order?.placed_by?.name?.trim() ||
    order?.placed_by?.business_name?.trim() ||
    order?.user?.name?.trim() ||
    '';
  const label = [orgName, customerName].filter(Boolean).join(', ');

  if (label) return label;

  return order?.user?.mobile?.trim() || 'N/A';
}

function Orders() {
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [invoiceTotals, setInvoiceTotals] = useState({});
  const [invoiceTotalsLoaded, setInvoiceTotalsLoaded] = useState({});
  const invoiceFetchGenRef = useRef(0);
  // const [paymentStatuses, setPaymentStatuses] = useState({});
  // const paymentFetchGenRef = useRef(0);
  const [filters, setFilters] = useState(() => ({
    contractor_id: '',
    phone: '',
    order_id: '',
    date: '',
    service_id: '',
    worker_id: '',
    status: searchParams.get('status') || '',
    channel: searchParams.get('channel') || '',
    organization_id: searchParams.get('organization_id') || '',
  }));

  const statuses = ['DRAFT', 'PENDING', 'ACCEPTED', 'ON_THE_WAY', 'IN_PROGRESS', 'DONE', 'COMPLETE', 'CANCELED'];
  const channels = ['B2B', 'WEB', 'APP'];

  const navigate = useNavigate();

  const deepLinkKey = [
    searchParams.get('status') || '',
    searchParams.get('channel') || '',
    searchParams.get('organization_id') || '',
  ].join('|');

  useEffect(() => {
    const [nextStatus, nextChannel, nextOrgId] = deepLinkKey.split('|');
    setFilters((prev) => {
      if (
        prev.status === nextStatus &&
        prev.channel === nextChannel &&
        prev.organization_id === nextOrgId
      ) {
        return prev;
      }
      return {
        ...prev,
        status: nextStatus,
        channel: nextChannel,
        organization_id: nextOrgId,
      };
    });
    setPage(1);
  }, [deepLinkKey]);

  const fetchOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${config.apiUrl}/api/v1/admin/orders?page=${page}&limit=10`;

      // Add filters to URL if they exist
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          url += `&${key}=${filters[key]}`;
        }
      });

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      console.log(result);
      if (response.ok) {
        // Handle both old format (array) and new format ({ success: true, data: [...] })
        const ordersData = result.success ? result.data : result;
        setOrders(Array.isArray(ordersData) ? ordersData : []);
        const ordersList = Array.isArray(ordersData) ? ordersData : [];
        setHasMore(ordersList.length === 10); // If we get less than limit, we've reached the end
        toast.success('Orders fetched successfully');
      } else {
        toast.error(result.message || 'Failed to fetch orders');
      }
    } catch (err) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Fetch invoice totals for visible orders so the Amount column shows actual invoiced amounts
  useEffect(() => {
    const gen = ++invoiceFetchGenRef.current;
    setInvoiceTotals({});
    setInvoiceTotalsLoaded({});

    if (orders.length === 0) return;

    orders.forEach(async (order) => {
      try {
        const { ok, body } = await getOrderFinancialsMaster(String(order.order_id));
        if (gen !== invoiceFetchGenRef.current) return;

        if (ok && body.success !== false) {
          const master = body.data ?? body;
          const payCmp = master?.payment?.comparison;
          const total = payCmp?.amount_total_cents ?? payCmp?.invoice_total_cents ?? master?.customer_invoice?.breakdown?.total_amount;
          if (total != null && total > 0) {
            setInvoiceTotals((prev) => ({ ...prev, [order.order_id]: Math.round(Number(total)) }));
          }
        }
      } catch {
        // Ignore — will fall back to expected_amount
      } finally {
        if (gen === invoiceFetchGenRef.current) {
          setInvoiceTotalsLoaded((prev) => ({ ...prev, [order.order_id]: true }));
        }
      }
    });

    return () => { invoiceFetchGenRef.current++; };
  }, [orders]);

  // // Fetch Stripe payment statuses for orders that have a Stripe payment method
  // useEffect(() => {
  //   const gen = ++paymentFetchGenRef.current;
  //   setPaymentStatuses({});

  //   const stripeOrders = orders.filter(
  //     (o) => o.payment_method && typeof o.payment_method === 'string' && o.payment_method.startsWith('pm_')
  //   );

  //   if (stripeOrders.length === 0) return;

  //   // Set loading state for all stripe orders
  //   const loadingState = {};
  //   stripeOrders.forEach((o) => {
  //     loadingState[o.order_id] = { status: null, loading: true };
  //   });
  //   setPaymentStatuses(loadingState);

  //   // Fetch payment ledger for each stripe order
  //   stripeOrders.forEach(async (order) => {
  //     try {
  //       const { ok, body } = await getOrderPaymentsLedger(String(order.order_id));
  //       if (gen !== paymentFetchGenRef.current) return;

  //       if (ok && body.success !== false) {
  //         const ledger = body.data ?? body;
  //         const orderPaymentStatus = ledger?.payment_status;
  //         const payments = Array.isArray(ledger?.payments) ? ledger.payments : [];

  //         // Use order-level payment_status, or derive from latest payment
  //         let displayStatus = orderPaymentStatus;
  //         if (!displayStatus && payments.length > 0) {
  //           displayStatus = payments[0]?.status;
  //         }

  //         setPaymentStatuses((prev) => ({
  //           ...prev,
  //           [order.order_id]: { status: displayStatus || null, loading: false },
  //         }));
  //       } else {
  //         setPaymentStatuses((prev) => ({
  //           ...prev,
  //           [order.order_id]: { status: null, loading: false, error: true },
  //         }));
  //       }
  //     } catch {
  //       if (gen !== paymentFetchGenRef.current) return;
  //       setPaymentStatuses((prev) => ({
  //         ...prev,
  //         [order.order_id]: { status: null, loading: false, error: true },
  //       }));
  //     }
  //   });

  //   return () => { paymentFetchGenRef.current++; };
  // }, [orders]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPage(1); // Reset to first page when filters change
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'ACCEPTED':
        return 'bg-blue-100 text-blue-800';
      case 'ON_THE_WAY':
        return 'bg-indigo-100 text-indigo-800';
      case 'IN_PROGRESS':
        return 'bg-purple-100 text-purple-800';
      case 'DONE':
        return 'bg-emerald-100 text-emerald-800';
      case 'COMPLETE':
        return 'bg-green-100 text-green-800';
      case 'CANCELED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // const getPaymentBadge = (order) => {
  //   const pm = order.payment_method;

  //   // No Stripe payment method → Outside Stripe
  //   if (!pm || typeof pm !== 'string' || !pm.startsWith('pm_')) {
  //     return (
  //       <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-600">
  //         Outside Stripe
  //       </span>
  //     );
  //   }

  //   // Has Stripe payment method — check fetched status
  //   const ps = paymentStatuses[order.order_id];

  //   if (!ps || ps.loading) {
  //     return (
  //       <span className="px-2 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-400">
  //         <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
  //           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
  //           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  //         </svg>
  //         Checking
  //       </span>
  //     );
  //   }

  //   if (ps.error || !ps.status) {
  //     return (
  //       <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-50 text-indigo-700">
  //         Via Stripe
  //       </span>
  //     );
  //   }

  //   const status = ps.status.toUpperCase();
  //   let colorClass, label;

  //   switch (status) {
  //     case 'PAID':
  //     case 'SUCCEEDED':
  //       colorClass = 'bg-green-100 text-green-800';
  //       label = 'Paid';
  //       break;
  //     case 'PROCESSING':
  //       colorClass = 'bg-yellow-100 text-yellow-800';
  //       label = 'Processing';
  //       break;
  //     case 'AUTHORIZED':
  //     case 'REQUIRES_CAPTURE':
  //       colorClass = 'bg-blue-100 text-blue-800';
  //       label = 'Authorized';
  //       break;
  //     case 'PAYMENT_FAILED':
  //     case 'FAILED':
  //       colorClass = 'bg-red-100 text-red-800';
  //       label = 'Failed';
  //       break;
  //     case 'CANCELED':
  //     case 'CANCELLED':
  //       colorClass = 'bg-red-100 text-red-800';
  //       label = 'Canceled';
  //       break;
  //     case 'REQUIRES_PAYMENT_METHOD':
  //       colorClass = 'bg-orange-100 text-orange-800';
  //       label = 'Needs Payment';
  //       break;
  //     default:
  //       colorClass = 'bg-purple-100 text-purple-800';
  //       label = status.replace(/_/g, ' ').replace(/\b\w+/g, w => w[0] + w.slice(1).toLowerCase());
  //   }

  //   return (
  //     <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${colorClass}`}>
  //       {label}
  //     </span>
  //   );
  // };

  return (
    <div className="min-h-screen bg-gray-100">
      <Topbar />
      <ToastContainer />

      <main className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <h1 className="text-2xl font-bold">Orders Management</h1>
            <Link
              to="/orders/new"
              className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700"
            >
              Create order for customer
            </Link>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <input
              type="text"
              placeholder="Order ID"
              value={filters.order_id}
              onChange={(e) => handleFilterChange('order_id', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <input
              type="text"
              placeholder="Phone Number"
              value={filters.phone}
              onChange={(e) => handleFilterChange('phone', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All Statuses</option>
              {statuses.map(status => (
                <option key={status} value={status}>{status.replace(/_/g, ' ').replace(/\b\w+/g, w => w[0] + w.slice(1).toLowerCase())}</option>
              ))}
            </select>
            <select
              value={filters.channel}
              onChange={(e) => handleFilterChange('channel', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All Channels</option>
              {channels.map((channel) => (
                <option key={channel} value={channel}>{channel}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Organization ID (B2B)"
              value={filters.organization_id}
              onChange={(e) => handleFilterChange('organization_id', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* Orders Table */}
          {loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th> */}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr
                      key={order.order_id}
                      onClick={() => navigate(`/orders/${order.order_id}`)}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">{order.order_id}</td>
                      <td className="px-6 py-4">{order.service?.title}</td>
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {getCustomerDisplayLabel(order)}
                        </p>
                        {(order.placed_by?.mobile || order.user?.mobile) && (
                          <p className="text-xs text-gray-500">{order.placed_by?.mobile || order.user?.mobile}</p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                          {order.status.replace(/_/g, ' ').replace(/\b\w+/g, w => w[0] + w.slice(1).toLowerCase())}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {order.amount != null
                          ? formatCentsAsCurrency(order.amount, order.service?.currency || 'CAD')
                          : invoiceTotalsLoaded[order.order_id]
                            ? formatCentsAsCurrency(invoiceTotals[order.order_id] ?? order.expected_amount, order.service?.currency || 'CAD')
                            : <span className="inline-block h-5 w-16 bg-gray-200 rounded animate-pulse" />
                        }
                      </td>
                      {/* <td className="px-6 py-4">{getPaymentBadge(order)}</td> */}
                      <td className="px-6 py-4">{formatEasternDate(order.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="flex justify-between items-center mt-4">
            <button
              onClick={() => setPage(prev => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            <span>Page {page}</span>
            <button
              onClick={() => setPage(prev => prev + 1)}
              disabled={!hasMore}
              className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Orders;
